'use client'

import { CoverageGauge } from './CoverageGauge'
import { ResultCard, ResultRow, Takeaway } from './fields'
import { ScheduleCallCTA } from '../../../controls/ScheduleCallCTA'
import { CTA } from '../../../utils/ctas'
import { months, signed, usd, usd0 } from '../_utils/format'
import type { CareerBreakResults, CareerBreakState } from '../types'

export function ResultsPanel({ state, results }: { state: CareerBreakState; results: CareerBreakResults }) {
  const breakLabel = months(state.breakMonths)

  return (
    <div className="flex flex-col gap-4">
      <section className="dark rounded-xl p-6 text-center bg-dark-gradient tablet:p-8">
        <CoverageGauge coverage={results.coverage} verdict={results.verdict} />

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-white/[0.06] p-4">
            <p className="text-body-xs text-white/60 uppercase">Cash you can use</p>
            <p className="mt-1 text-body-lg font-semibold text-foreground tabular-nums">{usd(results.available)}</p>
          </div>
          <div className="rounded-lg bg-white/[0.06] p-4">
            <p className="text-body-xs text-white/60 uppercase">The break costs</p>
            <p className="mt-1 text-body-lg font-semibold text-foreground tabular-nums">{usd(results.totalNeed)}</p>
          </div>
        </div>

        <div className="mt-6 text-left">
          <p className="text-body-xs text-green-lighter-2 uppercase">Based on what you entered</p>
          <p className="text-title-sm mt-2 text-foreground">{results.verdictHeadline}</p>
          <p className="mt-2.5 text-body-sm text-white/70">{results.verdictDetail}</p>
        </div>
      </section>

      {results.showAdvisorCard && (
        <section className="rounded-xl border border-border bg-gradient-to-br from-bone-light to-bone-warm p-6 text-center">
          <h2 className="text-title-sm text-gray-darker">{results.advisorHeadline}</h2>
          <p className="mx-auto mt-2 mb-4 max-w-130 text-subtitle text-neutral-dark">
            Sequencing which accounts to draw down, timing the gap in coverage, and protecting the retirement plan while
            you&apos;re out — that&apos;s the part worth talking through with a fiduciary.
          </p>
          <ScheduleCallCTA
            segmentElement="Career Break Results CTA"
            baseUrl={CTA.SCHEDULE_A_CALL}
            size="cta-sm"
            variant="cta-dark"
          >
            Book a free intro call →
          </ScheduleCallCTA>
          <p className="mx-auto mt-4 max-w-130 border-t border-border pt-4 text-body-xs text-neutral-dark">
            NerdWallet Wealth Partners, LLC (&quot;NWWP&quot;) is an SEC-registered investment adviser. Registration
            does not imply skill or training. All investments involve risk, including potential loss of principal.
          </p>
        </section>
      )}

      <ResultCard title="The cash math" subtitle={`What a ${breakLabel} break does to your monthly cash flow.`}>
        <ResultRow
          label="Household take-home during the break"
          tooltip="What your household still brings in each month while you are out, after federal tax, state tax and FICA on whatever income continues. Zero if nobody is earning."
          value={signed(results.breakNetMonthly, '+')}
        />
        <ResultRow label="Household spending today" value={signed(state.monthlySpend, '-')} />
        <ResultRow label="Spending that stops (childcare, commute)" value={signed(results.savingsMonthly, '+')} />
        <ResultRow
          label="Health coverage change"
          tooltip="Your new premium minus what already comes out of your paycheck today."
          value={signed(results.healthDelta, results.healthDelta >= 0 ? '-' : '+')}
          tone={results.healthDelta > 0 ? 'negative' : 'positive'}
        />
        <ResultRow
          label="Monthly shortfall to fund"
          tooltip="What it costs you to live each month, minus whatever your household still earns while you're out. Spending that stops and the change in your health insurance costs are already baked in. This is the number your savings has to cover."
          value={
            results.signedGapMonthly > 0
              ? `${usd0(results.signedGapMonthly)}/mo`
              : `${usd0(-results.signedGapMonthly)}/mo surplus`
          }
          tone={results.signedGapMonthly > 0 ? 'negative' : 'positive'}
        />
        <ResultRow
          label="Total cost of the whole break"
          tooltip="The cash you need to cover the gap for every month you are out. It does not include the long-term retirement cost, which is in the next card."
          value={usd0(results.totalNeed)}
        />
        <ResultRow label="Your cash + taxable investments" value={usd0(state.liquid)} />
        <ResultRow
          label="Less the emergency reserve you keep"
          tooltip="An emergency fund insures against losing income. A break makes that a certainty rather than a risk, so the same dollars shouldn't do both jobs. NerdWallet's guidance is three to six months of expenses; the default here is three."
          value={signed(results.reserve, '-')}
        />
        <ResultRow label="Cash you can put toward the break" value={usd0(results.available)} />
        <ResultRow
          label="Cash left when you go back to work"
          tooltip="Your starting cash minus what the break draws down. If you asked for an ending cushion in Advanced, the verdict checks against this."
          value={usd0(results.projectedEndCash)}
          tone={results.projectedEndCash >= results.reserve + state.endingCushion ? 'positive' : 'negative'}
        />
        <ResultRow
          label="How long your money actually lasts"
          tooltip="Your cash after setting aside the emergency reserve, divided by the monthly shortfall. If it's longer than the break you want, the break itself is funded — any cushion you asked for is checked separately."
          value={months(results.runwayMonths)}
          tone={results.runwayMonths >= state.breakMonths ? 'positive' : 'negative'}
        />

        <Takeaway headline="Stepping back is partly a tax cut.">
          Your household tax bill drops from {usd0(results.taxNow)} to {usd0(results.taxDuringBreak)} a year —{' '}
          <>
            {usd0(results.taxNow - results.taxDuringBreak)} you were never going to spend. That is why giving up{' '}
            {usd0(state.income)} of salary does not put a {usd0(state.income)} hole in your budget.
          </>
        </Takeaway>
      </ResultCard>

      <ResultCard
        title="The part that shows up later"
        subtitle={`Measured at age ${state.retirementAge}, in tomorrow's dollars.`}
      >
        <p className="mb-1 text-body-xs font-bold text-neutral-dark uppercase">Retirement</p>
        <ResultRow
          label="Contributions + match you won't make"
          tooltip="Your own 401(k) contributions plus the employer match, for every month you are off payroll. Raw dollars, before any growth."
          value={usd0(results.missedTotal)}
        />
        <ResultRow
          label="Those dollars, grown to retirement"
          tooltip="The skipped contributions and match, compounded at your assumed return until retirement. This is why a one-year gap costs far more than one year of contributions."
          value={usd(results.missedFutureValue)}
        />
        <ResultRow
          label="Opportunity cost of the cash you spend"
          tooltip="Assumes every dollar you draw down would otherwise have stayed invested at your assumed return until retirement. If part of that cash was sitting in a savings account anyway, the real number is smaller."
          value={usd(results.drawdownFutureValue)}
        />
        <ResultRow
          label={`Total long-term cost at ${state.retirementAge}`}
          tooltip="Retirement-account dollars and taxable cash combined. The retirement projection further down uses only the retirement-account part, so the two are not double counted."
          value={usd(results.longTermCost)}
        />

        <p className="mt-5 mb-1 text-body-xs font-bold text-neutral-dark uppercase">
          Where you land at {state.retirementAge}
        </p>
        <ResultRow label="Retirement accounts today" value={usd0(state.retirementBalance)} />
        <ResultRow
          label={`Projected at ${state.retirementAge} if you don't take the break`}
          value={usd(results.projectedNoBreak)}
        />
        <ResultRow
          label={`Projected at ${state.retirementAge} if you do`}
          tooltip="Retirement accounts only. The cash you spend down is an opportunity cost against taxable savings, so it is shown separately rather than subtracted from this balance."
          value={usd(results.projectedWithBreak)}
        />

        <p className="mt-5 mb-1 text-body-xs font-bold text-neutral-dark uppercase">Income</p>
        <ResultRow
          label="Pay gap after you return"
          tooltip="Pre-tax earnings lost while your pay climbs back to where it was."
          value={results.payGap > 0 ? usd(results.payGap) : results.payGap < 0 ? `+${usd(-results.payGap)}` : '$0'}
          tone={results.payGap > 0 ? 'negative' : 'positive'}
        />
        <ResultRow
          label="Social Security at full retirement age"
          tooltip="Social Security averages your highest 35 years of indexed earnings. If you'll still have 35 or more working years, the effect is probably small — though a high-earning year you skip can still displace a lower one. Your statement at ssa.gov has the precise figure."
          value={results.socialSecurityReduced ? 'Likely reduced' : 'Little or no change'}
          tone={results.socialSecurityReduced ? 'negative' : 'positive'}
        />

        <Takeaway headline={`${usd(results.longTermCost)} is the number people don't see coming.`}>
          {usd0(results.missedTotal)} of skipped contributions and match becomes {usd(results.missedFutureValue)} by age{' '}
          {state.retirementAge} once you let it compound at {state.returnPct.toFixed(1)}%.{' '}
          <>{results.socialSecurityNote}</>
        </Takeaway>
      </ResultCard>

      <ResultCard title="What could make this work">
        {results.levers.map((lever) => (
          <ResultRow
            key={lever.label}
            label={lever.label}
            tooltip={lever.tip}
            value={lever.value}
            emphasis={lever.emphasis}
          />
        ))}
      </ResultCard>
    </div>
  )
}

/** Exported for the assumptions footnote, which sits outside the results column. */
export function AssumptionsNote() {
  return (
    <div className="mx-auto max-w-155 space-y-3 px-2 text-body-xs text-neutral-dark">
      <p>
        NerdWallet Wealth Partners, LLC (&quot;NWWP&quot;) is an SEC-registered investment adviser. Registration does
        not imply a certain level of skill or training. This is an educational tool provided by NWWP only and does not
        provide legal, tax, investment, or estate planning advice. Results are hypothetical estimates based on the
        information you provided, are not recommendations, and do not determine whether any strategy is appropriate for
        you. Financial planning strategies are highly individualized and depend on your financial circumstances, family
        situation, state law, and tax considerations. Tax laws and other assumptions used in this tool may change over
        time. Please consult qualified legal, tax, and financial professionals before implementing any strategy
        discussed.
      </p>
      <p>
        Uses 2026 federal tax brackets, standard deductions and the 0.9% Additional Medicare Tax (IRS Rev. Proc.
        2025-32), the 2026 Social Security wage base of $184,500, 2026 401(k) limits including age 50+ and age 60-63
        catch-ups,{' '}
        <a
          href="https://files.kff.org/attachment/Employer-Health-Benefits-Survey-2025-Annual-Survey-Summary-of-Findings.pdf"
          className="underline"
        >
          KFF 2025 employer premium averages
        </a>{' '}
        <>
          for COBRA, and the 2026 national average benchmark marketplace premium quoted for a 40-year-old. Marketplace
          subsidy eligibility uses the 2025 federal poverty guidelines, which are the ones that apply to the 2026 plan
          year; enhanced premium tax credits expired December 31, 2025, so the 400% cliff applies. Taxes assume the
          break covers a full calendar year, and marketplace eligibility uses income during the break rather than
          full-year income. This may make a mid-year break look more affordable than it is.
        </>
      </p>
    </div>
  )
}
